import React, { useState, useContext, useRef, useEffect } from 'react';
import { t } from '@lingui/macro';
import { useQuery } from '@apollo/client';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import KDSTopNav from '@decisiv/ui-components/lib/components/TopNav';

import { CurrentInvitationContext, logout } from 'utils/Invitation';
import { useCurrentUserContext } from 'utils/User';

import useSentinelTeam from 'hooks/useSentinelTeam';
import userInfoQuery from 'utils/User/currentUserProfile.graphql';

import useIntersectionObserver from 'utils/useIntersectionObserver';
import TeamsDropdown from './TeamsDropdown';
import BrandInfo from './BrandInfo';
import CurrentCallInfo from './CurrentCallInfo';
import SearchLink from './SearchLink';
import NotificationCenter from './NotificationCenter';
import UserMenu from './UserMenu';
import AmazonConnectMenu from './AmazonConnectMenu';
import CaseLinks from './CaseLinks';

function TopNav() {
  const [firstBoundingWidth, setFirstBoundingWidth] = useState(null);

  const { id: invitationId, authenticationKey } = useContext(
    CurrentInvitationContext,
  );
  const { id: userId, isSuperUser } = useCurrentUserContext();

  const { hasSentinelTeam, belongsToOemSegment } = useSentinelTeam();

  const { loading, data } = useQuery(userInfoQuery, {
    fetchPolicy: 'cache-and-network',
    context: { ignoreInvitationAuth: true },
  });

  const showEndSession =
    invitationId || (!loading && data && authenticationKey);

  const ref = useRef(null);

  const isIntersecting = useIntersectionObserver(ref, { threshold: 1.0 });
  const isFullyVisible = isIntersecting && firstBoundingWidth === null;

  useEffect(() => {
    if (!isIntersecting && !firstBoundingWidth) {
      setFirstBoundingWidth(ref.current?.getBoundingClientRect()?.width);
    }
  }, [isIntersecting, firstBoundingWidth]);

  useEffect(() => {
    const handleResize = () => {
      if (ref.current?.getBoundingClientRect()?.width > firstBoundingWidth) {
        setFirstBoundingWidth(null);
      }
    };

    if (firstBoundingWidth) {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [firstBoundingWidth]);

  return (
    <div ref={ref}>
      <KDSTopNav>
        <Flex
          css={`
            height: 100%;
          `}
          alignItems="center"
        >
          <TeamsDropdown>
            <BrandInfo variant={isFullyVisible ? 'full' : 'compact'} />
            <CurrentCallInfo />
          </TeamsDropdown>
        </Flex>
        <Flex
          css={`
            height: 100%;
          `}
        >
          {userId && (
            <>
              <SearchLink />
              <NotificationCenter />
              <UserMenu variant={isFullyVisible ? 'full' : 'compact'} />
              <AmazonConnectMenu />
              {hasSentinelTeam && (isSuperUser || !belongsToOemSegment) && (
                <CaseLinks />
              )}
            </>
          )}
          {showEndSession && (
            <Flex alignItems="center" marginRight={2}>
              <Button
                kind="secondary"
                onClick={logout}
                size="small"
                text={t`End Session`}
                variant="inverted"
              />
            </Flex>
          )}
        </Flex>
      </KDSTopNav>
    </div>
  );
}

export default TopNav;
